import React, {FC} from "react"
import {Body, H2, H3, Label} from "@irati/ui/src/Typography"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {FormattedCurrency, FormattedMessage} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {CashbackTransactionStatus, Wallet} from "@irati/types"
import {useAppRouteContext} from "@irati/utils/history"
import authentication from "@irati/auth"
import {useCashbackTransactionTotalAmount} from "@irati/cashback/hooks/cashback"
import {walletTracking} from "@irati-tracking/wallet"
import TriggerCashbackModal from "../CashbackModal/TriggerCashbackModal"
import {ActiveCard, Trigger} from "./styled"

export interface Props {
  wallet: Wallet
  active: boolean
}

const CashbackWallet: FC<Props> = ({wallet, active}: Props) => {
  const {apps} = useAppRouteContext()
  const source = "cashback_wallet"
  const theme = useTheme()
  const {amount = 0, currency} = wallet
  const hasZeroState = wallet.amount === 0
  const {data} = useCashbackTransactionTotalAmount({
    userId: authentication.user().uuid!,
    status: CashbackTransactionStatus.StatusPending,
    currency
  })

  return (
    <ActiveCard active={active} onClick={() => walletTracking.clickCashbackCard()}>
      <Trigger>
        <TriggerCashbackModal />
      </Trigger>

      <H3 color={theme.colors.utility.ink} marginBottom={2}>
        <FormattedMessage id="wallet.walletBalanceCard.cashback.title" />
      </H3>

      <Flex justifyContent="space-between">
        <Flex direction="column">
          <Label>
            <FormattedMessage id="wallet.walletBalanceCard.available.header" />
          </Label>
          <H2>{currency && <FormattedCurrency isCents={true} value={amount} currency={currency} />}</H2>
        </Flex>

        {!!data?.amount && (
          <Flex direction="column">
            <Label color={theme.colors.greys.grey50}>
              <FormattedMessage id="wallet.walletBalanceCard.pending.header" />
            </Label>
            <H2 color={theme.colors.greys.grey50}>
              <FormattedCurrency isCents={true} value={data?.amount} currency={currency} />
            </H2>
          </Flex>
        )}
      </Flex>

      {hasZeroState ? (
        <Body color={theme.colors.utility.contrast} marginTop={2} marginBottom={2}>
          <FormattedMessage id="wallet.cashbackCard.zeroState.info.text" />
        </Body>
      ) : (
        <Body color={theme.colors.utility.contrast} marginTop={2} marginBottom={2}>
          <FormattedMessage id="wallet.cashbackCard.nonZeroState.info.text" />
        </Body>
      )}

      <div>
        <ButtonRouterLink
          primary={hasZeroState}
          full
          stopPropagation
          onClick={() => {
            walletTracking.clickCashbackCTA("earn_cashback", source)
          }}
          to={"/categories?type=cashback"}
          app={apps.PERKS}>
          <FormattedMessage id="wallet.cashbackCard.ctaSection.earn.cta.title" />
        </ButtonRouterLink>

        {!hasZeroState && (
          <ButtonRouterLink
            primary
            full
            stopPropagation
            onClick={() => {
              walletTracking.clickCashbackCTA("spend_cashback", source)
            }}
            to={"/categories?type=gift_cards"}
            app={apps.PERKS}
            marginTop={2}>
            <FormattedMessage id="wallet.cashbackCard.ctaSection.spend.cta.title" />
          </ButtonRouterLink>
        )}
      </div>
    </ActiveCard>
  )
}

export default CashbackWallet
